import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Chart from "react-apexcharts";
import toast from 'react-hot-toast';
import moment from 'moment';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content'
import PageTitleAndSnackBar from '../components/common/PageTitleAndSnackBar';
import AlertBox from '../components/alert/AlertBox';
import { reportsService } from '../services/reports.service';
import { SOMETHING_WENT_WRONG } from '../constants/strings';

const num = (v) => Number(v) || 0;
const inr = (v, digits = 0) => `₹${num(v).toLocaleString("en-IN", { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
const count = (v) => num(v).toLocaleString("en-IN");

const BOOKING_STATUSES = [
  { key: "pending_bookings", status: "pending", label: "Pending", color: "#c47c1a", bg: "#fef9ec" },
  { key: "on_hold_bookings", status: "on_hold", label: "On Hold", color: "#6b7280", bg: "#f4f6fb" },
  { key: "confirmed_bookings", status: "confirmed", label: "Confirmed", color: "var(--btn-bg)", bg: "#e6f1f8" },
  { key: "ticketed_bookings", status: "ticketed", label: "Ticketed", color: "#7c4dbd", bg: "#f4effc" },
  { key: "completed_bookings", status: "completed", label: "Completed", color: "#1a7a55", bg: "#edfaf4" },
  { key: "cancelled_bookings", status: "cancelled", label: "Cancelled", color: "#c45252", bg: "#fff4f4" },
  { key: "no_show_bookings", status: "no_show", label: "No Show", color: "#374151", bg: "#eceef2" },
];

const PAYMENT_STYLES = {
  paid: { bg: "#edfaf4", color: "#1a7a55" },
  pending: { bg: "#fef9ec", color: "#b45309" },
  failed: { bg: "#fff4f4", color: "#c45252" },
  refunded: { bg: "#e6f1f8", color: "var(--btn-bg)" },
};

const CITY_COLORS = ['#0E6A96', '#5b84c4', '#3a8f65', '#c47c1a', '#c45252', '#475569', '#7c4dbd', '#0e7ab8'];

const statusStyle = (status) =>
  BOOKING_STATUSES.find((s) => s.status == String(status).toLowerCase()) || { bg: "#f4f6fb", color: "#4b5563" };

const labelOf = (v) => String(v || "-").replace(/_/g, " ").toUpperCase();

const thStyle = {
  fontSize: "11px", fontWeight: "700", color: "#64748b",
  textTransform: "uppercase", letterSpacing: "0.6px",
  border: "none", padding: "10px 12px", background: "#f8f9fd", whiteSpace: "nowrap"
};
const tdStyle = { padding: "10px 12px", whiteSpace: "nowrap" };
const pill = (bg, color) => ({
  background: bg, color, borderRadius: "6px", padding: "3px 9px",
  fontSize: "11px", fontWeight: "700", display: "inline-block"
});
const mutedText = { fontSize: "11px", color: "#64748b" };

// Reusable section header icon box — soft fill instead of dark gradient
const SectionIcon = ({ icon }) => (
  <div style={{
    width: "34px", height: "34px", borderRadius: "10px",
    background: "#e8edf8", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center"
  }}>
    <i className={`bi ${icon}`} style={{ fontSize: "14px", color: "var(--btn-hover-bg)" }}></i>
  </div>
);

const SectionCard = ({ icon, title, subtitle, action, children }) => (
  <div className="card border-0 shadow-sm h-100 mb-0" style={{ borderRadius: "16px" }}>
    <div className="card-body p-4">
      <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <SectionIcon icon={icon} />
          <div>
            <h6 className="mb-0 fw-bold" style={{ color: "var(--btn-hover-bg)", fontSize: "14px" }}>{title}</h6>
            {subtitle && <span style={mutedText}>{subtitle}</span>}
          </div>
        </div>
        {action}
      </div>
      {children}
    </div>
  </div>
);

const EmptyState = ({ icon, text }) => (
  <div className="text-center py-5">
    <i className={`bi ${icon}`} style={{ fontSize: "32px", color: "#c9d1e0" }}></i>
    <div style={{ fontSize: "13px", color: "#64748b", marginTop: "6px" }}>{text}</div>
  </div>
);

const ProgressBar = ({ pct, color = "#5b84c4", height = 4 }) => (
  <div style={{ height, background: "#eef0f8", borderRadius: "4px", overflow: "hidden" }}>
    <div style={{
      height: "100%", borderRadius: "4px", background: color,
      width: `${Math.min(100, Math.max(0, pct))}%`, transition: "width 0.4s ease"
    }}></div>
  </div>
);

const Homepage = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const showSwal = (title, text, icon) => withReactContent(Swal).fire({ title, text, icon });

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const res = await reportsService.dashboard();
      if (res?.success && res.resData) {
        setData(res.resData);
        setLastUpdated(moment());
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const bookings = data?.bookings || {};
  const transactions = data?.transactions || {};
  const inventory = data?.inventory || {};
  const wallet = data?.wallet || {};
  const today = data?.today || {};
  const flights = data?.flights || {};
  const trend = data?.booking_trend || [];
  const recentBookings = data?.recent_bookings || [];
  const topRoutes = data?.top_routes || [];
  const topCities = data?.top_cities || [];

  const totalBookings = num(bookings.total_bookings);
  const fillRate = num(inventory.seat_fill_rate_pct);
  const netFlow = num(transactions.total_credits) - num(transactions.total_debits);
  const maxRouteBookings = Math.max(1, ...topRoutes.map((r) => num(r.total_bookings)));
  const totalCityBookings = topCities.reduce((sum, c) => sum + num(c.total_bookings), 0);

  const statusBreakdown = BOOKING_STATUSES.map((s) => ({ ...s, value: num(bookings[s.key]) }));
  const statusTotal = statusBreakdown.reduce((sum, s) => sum + s.value, 0);

  const kpis = [
    {
      label: "Total Bookings", icon: "bi-ticket-perforated-fill", iconBg: "#e6f1f8", iconColor: "var(--btn-bg)", borderTop: "#5b84c4",
      value: count(bookings.total_bookings),
      foot: <><b>{count(today.today_bookings)}</b> today · <b>{count(bookings.total_passengers)}</b> passengers</>,
    },
    {
      label: "Total Revenue", icon: "bi-currency-rupee", iconBg: "#edfaf4", iconColor: "#1a7a55", borderTop: "#3aaa7a",
      value: inr(bookings.total_revenue),
      foot: <><b>{inr(today.today_revenue)}</b> today · avg <b>{inr(bookings.avg_booking_value)}</b> / booking</>,
    },
    {
      label: "Wallet Balance", icon: "bi-wallet2", iconBg: "#fef9ec", iconColor: "#b45309", borderTop: "#e8b84b", to: "/wallet",
      value: inr(wallet.total_wallet_balance),
      foot: <>across <b>{count(wallet.total_suppliers)}</b> supplier{num(wallet.total_suppliers) == 1 ? "" : "s"}</>,
    },
    {
      label: "Seat Fill Rate", icon: "bi-pie-chart-fill", iconBg: "#e8f5fe", iconColor: "#0e7ab8", borderTop: "#4faad6",
      value: `${fillRate.toFixed(2)}%`,
      foot: <><b>{count(inventory.booked_seats)}</b> of <b>{count(inventory.total_seats)}</b> seats booked</>,
      progress: fillRate,
    },
  ];

  const miniStats = [
    // { label: "Active Flights", icon: "bi-airplane-fill", value: count(flights.active_flights), sub: `of ${count(flights.total_flights)} flights`, to: "/flight/list" },
    { label: "Supplier's Inventory", icon: "bi-airplane-fill", value: count(flights.active_flights), sub: `number of inventories`, to: "/flight/list" },
    { label: "My Inventory", icon: "bi-box-seam", value: count(inventory.total_inventory), sub: "number of inventories", to: "/inventory/list" },
    { label: "Total Seats", icon: "bi-grid-3x3-gap-fill", value: count(inventory.total_seats), sub: `${count(inventory.booked_seats)} booked` },
    { label: "Available Seats", icon: "bi-check2-square", value: count(inventory.available_seats), sub: "ready to sell" },
  ];

  const trendChart = {
    options: {
      chart: { id: "booking-trends", toolbar: { show: false }, fontFamily: "inherit" },
      xaxis: { categories: trend.map((t) => t.label), labels: { style: { colors: "#64748b", fontSize: "11px" } } },
      colors: ['#5b84c4', '#3a8f65'],
      stroke: { curve: 'smooth', width: [2, 3] },
      fill: { type: ['gradient', 'solid'], gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.05 } },
      markers: { size: 0, hover: { size: 5 } },
      dataLabels: { enabled: false },
      grid: { borderColor: "#f0f2f8", strokeDashArray: 4 },
      legend: { position: "top", horizontalAlign: "right", fontSize: "12px" },
      yaxis: [
        { seriesName: "Bookings", title: { text: "Bookings", style: { color: "#64748b", fontSize: "11px", fontWeight: 600 } }, labels: { style: { colors: "#64748b", fontSize: "11px" }, formatter: (v) => Math.round(v) }, min: 0, forceNiceScale: true },
        { seriesName: "Revenue", opposite: true, title: { text: "Revenue", style: { color: "#64748b", fontSize: "11px", fontWeight: 600 } }, labels: { style: { colors: "#64748b", fontSize: "11px" }, formatter: (v) => inr(v) }, min: 0 },
      ],
      tooltip: { theme: "light", shared: true, intersect: false, y: { formatter: (v, { seriesIndex }) => (seriesIndex == 1 ? inr(v) : count(v)) } },
    },
    series: [
      { name: "Bookings", type: "area", data: trend.map((t) => num(t.total_bookings)) },
      { name: "Revenue", type: "line", data: trend.map((t) => num(t.total_revenue)) },
    ],
  };

  const statusChart = {
    options: {
      labels: statusBreakdown.map((s) => s.label),
      colors: statusBreakdown.map((s) => s.color),
      legend: { show: false },
      dataLabels: { enabled: false },
      stroke: { width: 2 },
      plotOptions: { pie: { donut: { size: "72%", labels: { show: true, total: { show: true, label: "Bookings", fontSize: "12px", color: "#64748b", formatter: () => count(statusTotal) } } } } },
      tooltip: { y: { formatter: (v) => count(v) } },
    },
    series: statusBreakdown.map((s) => s.value),
  };

  const cityChart = {
    options: {
      labels: topCities.map((c) => c.city_name),
      colors: CITY_COLORS,
      legend: { show: false },
      dataLabels: { enabled: false },
      stroke: { width: 2 },
      plotOptions: { pie: { donut: { size: "70%", labels: { show: true, total: { show: true, label: "Bookings", fontSize: "12px", color: "#64748b", formatter: () => count(totalCityBookings) } } } } },
      tooltip: { y: { formatter: (v) => `${count(v)} bookings` } },
    },
    series: topCities.map((c) => num(c.total_bookings)),
  };

  if (loading && !data) {
    return (
      <>
        <PageTitleAndSnackBar pageTitle="Dashboard" />
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
          <span className="spinner-border" style={{ color: "#5b84c4" }}></span>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitleAndSnackBar pageTitle="Dashboard" />
      <AlertBox />

      <section className="section dashboard">
        {/* ── Header ── */}
        <div className="d-flex flex-wrap align-items-end justify-content-between gap-2 mb-3">
          <div>
            <h5 className="mb-0 fw-bold" style={{ color: "var(--btn-hover-bg)" }}>Your Business Snapshot</h5>
            <span style={{ fontSize: "12px", color: "#475569" }}>
              A quick overview of your travel business
              {lastUpdated ? ` · updated ${lastUpdated.format("hh:mm A")}` : ""}
            </span>
          </div>
          <button
            type="button"
            className="btn btn-sm fw-semibold d-flex align-items-center gap-2"
            onClick={loadDashboard}
            disabled={loading}
            style={{ background: "#fff", color: "#4b5563", border: "1.5px solid #dde3ef", borderRadius: "8px", fontSize: "12px", height: "32px", padding: "0 14px" }}
          >
            <i className={`bi bi-arrow-clockwise ${loading ? "spin-icon" : ""}`} style={{ color: "var(--btn-bg)" }}></i> Refresh
          </button>
        </div>
        <style>{`.spin-icon { display:inline-block; animation: dash-spin 0.8s linear infinite; } @keyframes dash-spin { to { transform: rotate(360deg); } }`}</style>

        <div className="row g-3">

          {/* ── KPI Cards ── */}
          {kpis.map((stat, i) => {
            const card = (
              <div className="card border-0 shadow-sm h-100 mb-0"
                style={{ borderRadius: "16px", borderTop: `3px solid ${stat.borderTop}`, transition: "transform 0.15s, box-shadow 0.15s" }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(14,106,150,0.09)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
              >
                <div className="card-body p-3">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <p className="mb-0" style={{ fontSize: "11px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.7px" }}>
                      {stat.label}
                    </p>
                    <div style={{
                      width: "42px", height: "42px", borderRadius: "12px",
                      background: stat.iconBg, flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center"
                    }}>
                      <i className={`bi ${stat.icon}`} style={{ fontSize: "18px", color: stat.iconColor }}></i>
                    </div>
                  </div>
                  <h4 className="fw-bold mb-1" style={{ color: "var(--btn-hover-bg)", fontSize: "22px" }}>{stat.value}</h4>
                  {stat.progress != null && <div className="my-2"><ProgressBar pct={stat.progress} color={stat.borderTop} /></div>}
                  <div style={{ fontSize: "11px", color: "#475569" }}>{stat.foot}</div>
                </div>
              </div>
            );
            return (
              <div className="col-xxl-3 col-md-6" key={i}>
                {stat.to ? <Link to={stat.to} style={{ textDecoration: "none" }}>{card}</Link> : card}
              </div>
            );
          })}

          {/* ── Inventory & Flights mini stats ── */}
          {miniStats.map((s, i) => {
            const body = (
              <div className="card border-0 shadow-sm h-100 mb-0" style={{ borderRadius: "14px" }}>
                <div className="card-body p-3 d-flex align-items-center gap-3">
                  <SectionIcon icon={s.icon} />
                  <div className="min-w-0">
                    <div style={{ fontSize: "11px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.6px" }}>{s.label}</div>
                    <div className="fw-bold" style={{ color: "var(--btn-hover-bg)", fontSize: "18px", lineHeight: 1.2 }}>{s.value}</div>
                    <div style={mutedText}>{s.sub}</div>
                  </div>
                </div>
              </div>
            );
            return (
              <div className="col-xxl-3 col-md-6" key={`mini-${i}`}>
                {s.to ? <Link to={s.to} style={{ textDecoration: "none" }}>{body}</Link> : body}
              </div>
            );
          })}

          {/* ── Booking Trends Chart ── */}
          <div className="col-xxl-8 col-xl-12">
            <SectionCard icon="bi-graph-up-arrow" title="Booking Trends & Revenue" subtitle="Month-wise, this year">
              {trend.length ? (
                <Chart options={trendChart.options} series={trendChart.series} type="line" height="320" />
              ) : (
                <EmptyState icon="bi-graph-up" text="No trend data yet" />
              )}
            </SectionCard>
          </div>

          {/* ── Booking Status ── */}
          <div className="col-xxl-4 col-xl-12">
            <SectionCard icon="bi-list-check" title="Booking Status" subtitle={`${count(totalBookings)} total bookings`}>
              {statusTotal > 0 ? (
                <Chart options={statusChart.options} series={statusChart.series} type="donut" height="200" />
              ) : (
                <EmptyState icon="bi-pie-chart" text="No bookings yet" />
              )}
              <div className="row g-2 mt-1">
                {statusBreakdown.map((s) => (
                  <div className="col-6" key={s.key}>
                    <div className="d-flex align-items-center justify-content-between" style={{ background: s.bg, borderRadius: "8px", padding: "6px 10px" }}>
                      <span style={{ fontSize: "12px", color: s.color, fontWeight: 600 }}>
                        <span style={{ display: "inline-block", width: 7, height: 7, borderRadius: "50%", background: s.color, marginRight: 6 }}></span>
                        {s.label}
                      </span>
                      <span style={{ fontSize: "13px", fontWeight: 700, color: s.color }}>{count(s.value)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          </div>

          {/* ── Recent Bookings Table ── */}
          <div className="col-xxl-8 col-xl-12">
            <SectionCard icon="bi-ticket-perforated-fill" title="Recent Bookings" subtitle="Latest bookings">
              {recentBookings.length ? (
                <div className="table-responsive">
                  <table className="table mb-0" style={{ fontSize: "13px" }}>
                    <thead>
                      <tr>
                        {["Booking ID", "Agent", "Route", "Departure", "Pax", "Amount", "Status", "Payment", "Booked On"].map((h) => (
                          <th key={h} style={thStyle}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {recentBookings.map((b) => {
                        const st = statusStyle(b.booking_status);
                        const pay = PAYMENT_STYLES[String(b.payment_status).toLowerCase()] || { bg: "#f4f6fb", color: "#4b5563" };
                        return (
                          <tr key={b.booking_ref_id} style={{ borderBottom: "1px solid #f0f2f8", verticalAlign: "middle" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#f8f9fe"}
                            onMouseLeave={e => e.currentTarget.style.background = ""}
                          >
                            <td style={tdStyle}>
                              <span style={pill("#e6f1f8", "var(--btn-bg)")}>{b.booking_ref_id}</span>
                            </td>
                            <td style={tdStyle}>
                              <div style={{ fontWeight: 600, color: "var(--btn-hover-bg)" }}>{b.agent_name}</div>
                              <div style={mutedText}>{b.agent_code}</div>
                            </td>
                            <td style={tdStyle}>
                              <span style={{ ...pill("#f4f6fb", "#4b5563"), fontSize: "12px" }}>
                                <i className="bi bi-airplane-fill me-1" style={{ color: "var(--btn-bg)", fontSize: "10px" }}></i>
                                {b.origin} → {b.destination}
                              </span>
                            </td>
                            <td style={{ ...tdStyle, color: "#4b5563" }}>{b.departure_date ? moment(b.departure_date).format("DD MMM YYYY") : "-"}</td>
                            <td style={{ ...tdStyle, color: "#4b5563", fontWeight: 600 }}>{count(b.passengers)}</td>
                            <td style={tdStyle}>
                              <span style={{ ...pill("#edfaf4", "#1a7a55"), fontSize: "12px" }}>
                                {b.currency_code && b.currency_code != "INR" ? `${b.currency_code} ${num(b.total_fare).toLocaleString("en-IN")}` : inr(b.total_fare)}
                              </span>
                            </td>
                            <td style={tdStyle}><span style={pill(st.bg, st.color)}>{labelOf(b.booking_status)}</span></td>
                            <td style={tdStyle}><span style={pill(pay.bg, pay.color)}>{labelOf(b.payment_status)}</span></td>
                            <td style={tdStyle}>
                              <div style={{ color: "#4b5563" }}>{moment(b.created_at).format("DD MMM YYYY")}</div>
                              <div style={mutedText}>{moment(b.created_at).format("hh:mm A")}</div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState icon="bi-ticket-perforated" text="No bookings yet" />
              )}
            </SectionCard>
          </div>

          {/* ── Top Cities ── */}
          <div className="col-xxl-4 col-xl-12">
            <SectionCard icon="bi-geo-alt-fill" title="Top Cities" subtitle="Bookings by destination city">
              {topCities.length ? (
                <>
                  <Chart options={cityChart.options} series={cityChart.series} type="donut" height="200" />
                  <div className="d-flex flex-column gap-2 mt-2">
                    {topCities.map((c, i) => (
                      <div key={`${c.city_name}-${i}`} className="d-flex align-items-center justify-content-between gap-2"
                        style={{ padding: "8px 10px", borderRadius: "10px", border: "1.5px solid #f0f2f8" }}>
                        <div className="d-flex align-items-center gap-2 min-w-0">
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: CITY_COLORS[i % CITY_COLORS.length], flexShrink: 0 }}></span>
                          <div className="min-w-0">
                            <div className="text-truncate" style={{ fontSize: "13px", fontWeight: 700, color: "var(--btn-hover-bg)" }}>{c.city_name}</div>
                            <div style={mutedText}>{c.country_name} · {count(c.total_passengers)} pax</div>
                          </div>
                        </div>
                        <div className="text-end">
                          <div style={{ fontSize: "12px", fontWeight: 700, color: "#1a7a55" }}>{inr(c.total_revenue)}</div>
                          <div style={mutedText}>{count(c.total_bookings)} booking{num(c.total_bookings) == 1 ? "" : "s"}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <EmptyState icon="bi-geo-alt" text="No city data yet" />
              )}
            </SectionCard>
          </div>

          {/* ── Top Routes ── */}
          <div className="col-xxl-8 col-xl-12">
            <SectionCard icon="bi-map-fill" title="Top Routes" subtitle="Ranked by bookings">
              {topRoutes.length ? (
                <div className="table-responsive">
                  <table className="table mb-0" style={{ fontSize: "13px" }}>
                    <thead>
                      <tr>
                        {["Route", "Bookings", "Active", "Cancelled", "Passengers", "Avg Fare", "Revenue", "Share"].map((h) => (
                          <th key={h} style={thStyle}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {topRoutes.map((r, i) => (
                        <tr key={`${r.origin}-${r.destination}-${i}`} style={{ borderBottom: "1px solid #f0f2f8", verticalAlign: "middle" }}>
                          <td style={tdStyle}>
                            <div className="d-flex align-items-center gap-2">
                              <div style={{
                                width: "30px", height: "30px", borderRadius: "9px", background: "#e6f1f8", flexShrink: 0,
                                display: "flex", alignItems: "center", justifyContent: "center"
                              }}>
                                <i className="bi bi-airplane-fill" style={{ fontSize: "12px", color: "var(--btn-bg)" }}></i>
                              </div>
                              <div>
                                <div style={{ fontWeight: 700, color: "var(--btn-hover-bg)" }}>{r.origin} → {r.destination}</div>
                                <div style={{ ...mutedText, textTransform: "capitalize" }}>{r.route_type}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ ...tdStyle, fontWeight: 700, color: "var(--btn-hover-bg)" }}>{count(r.total_bookings)}</td>
                          <td style={tdStyle}><span style={pill("#edfaf4", "#1a7a55")}>{count(r.active_bookings)}</span></td>
                          <td style={tdStyle}><span style={pill("#fff4f4", "#c45252")}>{count(r.cancelled_bookings)}</span></td>
                          <td style={{ ...tdStyle, color: "#4b5563" }}>{count(r.total_passengers)}</td>
                          <td style={{ ...tdStyle, color: "#4b5563" }}>{inr(r.avg_fare)}</td>
                          <td style={{ ...tdStyle, fontWeight: 700, color: "#1a7a55" }}>{inr(r.total_revenue)}</td>
                          <td style={{ ...tdStyle, minWidth: "110px" }}>
                            <ProgressBar pct={(num(r.total_bookings) / maxRouteBookings) * 100} />
                            <div style={mutedText}>
                              {totalBookings ? ((num(r.total_bookings) / totalBookings) * 100).toFixed(0) : 0}% of bookings
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState icon="bi-map" text="No route data yet" />
              )}
            </SectionCard>
          </div>

          {/* ── Wallet & Transactions ── */}
          <div className="col-xxl-4 col-xl-12">
            <SectionCard
              icon="bi-arrow-left-right"
              title="Wallet & Transactions"
              subtitle="Credits and debits"
              action={
                <Link to="/wallet">
                  <button className="btn btn-sm fw-semibold d-flex align-items-center gap-2"
                    style={{ background: "#f4f6fb", color: "#4b5563", border: "1.5px solid #dde3ef", borderRadius: "8px", fontSize: "12px", height: "32px", padding: "0 14px" }}>
                    Wallet <i className="bi bi-arrow-right" style={{ color: "var(--btn-bg)" }}></i>
                  </button>
                </Link>
              }
            >
              <div style={{ background: "#f8f9fe", borderRadius: "12px", padding: "14px 16px" }} className="mb-3">
                <div style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.6px" }}>Total Wallet Balance</div>
                <div className="fw-bold" style={{ fontSize: "22px", color: "var(--btn-hover-bg)" }}>{inr(wallet.total_wallet_balance, 2)}</div>
                <div style={mutedText}>{count(wallet.total_suppliers)} supplier wallet{num(wallet.total_suppliers) == 1 ? "" : "s"}</div>
              </div>

              <div className="row g-2 mb-3">
                <div className="col-6">
                  <div style={{ background: "#edfaf4", borderRadius: "10px", padding: "10px 12px" }}>
                    <div style={{ fontSize: "11px", fontWeight: 700, color: "#1a7a55" }}>
                      <i className="bi bi-arrow-down-left-circle-fill me-1"></i>CREDITS
                    </div>
                    <div className="fw-bold" style={{ fontSize: "15px", color: "#1a7a55" }}>{inr(transactions.total_credits)}</div>
                    <div style={mutedText}>{count(transactions.credit_count)} transactions</div>
                  </div>
                </div>
                <div className="col-6">
                  <div style={{ background: "#fff4f4", borderRadius: "10px", padding: "10px 12px" }}>
                    <div style={{ fontSize: "11px", fontWeight: 700, color: "#c45252" }}>
                      <i className="bi bi-arrow-up-right-circle-fill me-1"></i>DEBITS
                    </div>
                    <div className="fw-bold" style={{ fontSize: "15px", color: "#c45252" }}>{inr(transactions.total_debits)}</div>
                    <div style={mutedText}>{count(transactions.debit_count)} transactions</div>
                  </div>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-3" style={{ fontSize: "13px" }}>
                <span style={{ color: "#4b5563", fontWeight: 600 }}>Net Flow</span>
                <span className="fw-bold" style={{ color: netFlow >= 0 ? "#1a7a55" : "#c45252" }}>
                  {netFlow >= 0 ? "+" : "-"}{inr(Math.abs(netFlow))}
                </span>
              </div>

              <div style={{ borderTop: "1px dashed #e5e9f2", paddingTop: "12px" }}>
                <div className="d-flex justify-content-between mb-1" style={{ fontSize: "12px" }}>
                  <span style={{ color: "#4b5563", fontWeight: 600 }}>Seat Utilisation</span>
                  <span style={{ color: "var(--btn-hover-bg)", fontWeight: 700 }}>{fillRate.toFixed(2)}%</span>
                </div>
                <ProgressBar pct={fillRate} color="#4faad6" height={6} />
                <div className="d-flex justify-content-between mt-1" style={mutedText}>
                  <span>{count(inventory.booked_seats)} booked</span>
                  <span>{count(inventory.available_seats)} available</span>
                </div>
              </div>
            </SectionCard>
          </div>

        </div>
      </section>
    </>
  );
}

export default Homepage;
